import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { listExchange } from "@/lib/exchange.functions";
import { LISTING_TYPES, TYPE_LABELS, fmtAmount, type ListingType } from "@/lib/listing-meta";
import { ArrowRight, Banknote, Globe2, Search } from "lucide-react";
import { MarketingLayout as Layout, useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo, SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/deals/")({
  loader: async () => {
    try {
      const r = await listExchange({ data: {} });
      return { listings: r.listings };
    } catch {
      return { listings: [] as any[] };
    }
  },
  head: ({ loaderData }) => {
    const seo = buildSeo({
      path: "/deals",
      title: "Live Deals — Reality Twin Exchange",
      description:
        "Live B2B deals on the Reality Twin Exchange: acquisitions, partnerships, supply and investment listings from verified companies.",
    });
    const items = (loaderData?.listings ?? []).slice(0, 30);
    const scripts = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Reality Twin Exchange — Live Deals",
          url: `${SITE_URL}/deals`,
          description: "Live business listings on the Reality Twin network.",
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: items.length,
            itemListElement: items.map((l: any, i: number) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${SITE_URL}/deals/${l.id}`,
              name: l.title,
            })),
          },
        }),
      },
    ];
    return { meta: seo.meta, links: seo.links, scripts };
  },
  component: Page,
});

function Page() {
  const t = useHydratedTranslation();
  const fetchList = useServerFn(listExchange);
  const initial = Route.useLoaderData();
  const [items, setItems] = useState<any[]>(initial?.listings ?? []);
  const [type, setType] = useState<ListingType | "">("");
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [country, setCountry] = useState("");

  useEffect(() => {
    fetchList({
      data: {
        type: type || undefined,
        q: q || undefined,
        industry: industry || undefined,
        country: country || undefined,
      },
    })
      .then((r) => setItems(r.listings))
      .catch(() => {});
  }, [type, q, industry, country, fetchList]);

  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-6 pt-24 pb-16">
        <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight text-gradient">
          {t("routes.directory.deals.title")}
        </h1>
        <p className="mt-4 text-muted-foreground max-w-2xl text-lg">
          {t("routes.directory.deals.sub")}
        </p>

        <div className="mt-8 flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setType("")}
            className={`text-xs px-3.5 py-1.5 rounded-full border ${type === "" ? "brand-gradient text-black border-transparent" : "glass border-border text-muted-foreground hover:bg-white/5"}`}
          >
            {t("routes.directory.deals.all")}
          </button>
          {LISTING_TYPES.map((lt) => (
            <button
              key={lt}
              onClick={() => setType(lt)}
              className={`text-xs px-3.5 py-1.5 rounded-full border ${type === lt ? "brand-gradient text-black border-transparent" : "glass border-border text-muted-foreground hover:bg-white/5"}`}
            >
              {t(`routes.directory.types.${lt}`)}
            </button>
          ))}
          <div className="ml-auto flex flex-wrap gap-2">
            <input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder={t("routes.directory.deals.industryPh")}
              className="glass w-32 rounded-full border border-border px-3 py-1.5 text-xs focus:outline-none"
            />
            <input
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder={t("routes.directory.deals.countryPh")}
              className="glass w-28 rounded-full border border-border px-3 py-1.5 text-xs focus:outline-none"
            />
            <div className="glass rounded-full border border-border flex items-center gap-2 px-3 py-1.5">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("routes.directory.deals.searchPh")}
                className="bg-transparent text-xs focus:outline-none w-36"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.length === 0 && (
            <div className="col-span-full glass-strong rounded-2xl p-10 text-center text-sm text-muted-foreground">
              {t("routes.directory.deals.emptyPre")}
              <Link to="/auth" className="text-[oklch(0.82_0.18_168)] hover:underline">
                {t("routes.directory.deals.emptyCta")}
              </Link>
            </div>
          )}
          {items.map((l: any) => {
            const meta = TYPE_LABELS[l.type as ListingType];
            const typeLabel = meta ? t(`routes.directory.types.${l.type}`) : l.type;
            return (
              <Link
                key={l.id}
                to="/deals/$id"
                params={{ id: l.id }}
                className="glass-strong rounded-2xl p-5 hover:bg-white/5 transition flex flex-col"
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-xs px-2.5 py-1 rounded-md glass"
                    style={{ color: `oklch(0.82 0.15 ${meta?.hue ?? "168"})` }}
                  >
                    {typeLabel}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">{l.company_name}</span>
                </div>
                <div className="font-display text-lg mt-3">{l.title}</div>
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  {fmtAmount(l.amount_min, l.amount_max, l.currency) && (
                    <span className="inline-flex items-center gap-1">
                      <Banknote className="h-3 w-3" />{" "}
                      {fmtAmount(l.amount_min, l.amount_max, l.currency)}
                    </span>
                  )}
                  {l.industry && <span>{l.industry}</span>}
                  {l.country && (
                    <span className="inline-flex items-center gap-1">
                      <Globe2 className="h-3 w-3" /> {l.country}
                    </span>
                  )}
                </div>
                {l.description && (
                  <p className="mt-3 text-sm text-muted-foreground line-clamp-3 flex-1">
                    {l.description}
                  </p>
                )}
                <div className="mt-4 text-xs text-[oklch(0.82_0.18_168)] flex items-center gap-1">
                  {t("routes.directory.deals.view")} <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 glass-strong rounded-2xl p-8 text-center">
          <div className="font-display text-2xl font-semibold">
            {t("routes.directory.deals.ctaTitle")}
          </div>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">
            {t("routes.directory.deals.ctaSub")}
          </p>
          <Link
            to="/auth"
            className="mt-5 inline-flex items-center gap-1.5 rounded-full brand-gradient text-black px-6 py-2.5 text-sm font-medium"
          >
            {t("routes.directory.deals.ctaBtn")} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
